import { z } from "zod";
import {
  MAX_DASHBOARD_STATUSES,
  analyticsPeriodProblem,
  isIsoDate,
  type AbcClass,
  type CoverageBand,
  type PortfolioBucket,
} from "@cx/domain";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { SalesOrderStatusRef } from "./sales-orders";

// ===== BI histórico (T3.9, RF-BI-10..12) =====

/** Canal "venda direta (ERP)" na URL (no lago é a loja vazia). */
export const DIRECT_CHANNEL_KEY = "direct";
export const MAX_ANALYTICS_CHANNELS = 30;

const statuses = z
  .string()
  .max(MAX_DASHBOARD_STATUSES * 27 + 10)
  .transform((v) => v.split(",").filter(Boolean))
  .pipe(
    z
      .array(ulidParam)
      .min(1, "Escolha as situações")
      .max(MAX_DASHBOARD_STATUSES, `No máximo ${MAX_DASHBOARD_STATUSES} situações`)
      .refine((l) => new Set(l).size === l.length, "Situação repetida"),
  )
  .optional();

/** `channels` = `direct` e/ou ids de loja, por vírgula; ausente = todos. Sai como chave de loja (`''` = direta). */
const channels = z
  .string()
  .max(MAX_ANALYTICS_CHANNELS * 27 + 10)
  .transform((v) => v.split(",").filter(Boolean))
  .pipe(
    z
      .array(z.union([z.literal(DIRECT_CHANNEL_KEY), ulidParam], "Canal inválido"))
      .min(1, "Escolha os canais")
      .max(MAX_ANALYTICS_CHANNELS, `No máximo ${MAX_ANALYTICS_CHANNELS} canais`)
      .refine((l) => new Set(l).size === l.length, "Canal repetido"),
  )
  .transform((l) => l.map((k) => (k === DIRECT_CHANNEL_KEY ? "" : k)))
  .optional();

const period = {
  from: businessDate.optional(),
  to: businessDate.optional(),
  channels,
  statuses,
};

function refinePeriod(v: { from?: string; to?: string }, ctx: z.RefinementCtx) {
  if (v.from === undefined && v.to === undefined) return;
  if (v.from === undefined)
    return ctx.addIssue({ code: "custom", path: ["from"], message: "Informe o início do período" });
  if (v.to === undefined)
    return ctx.addIssue({ code: "custom", path: ["to"], message: "Informe o fim do período" });
  if (!isIsoDate(v.from) || !isIsoDate(v.to)) return;
  const p = analyticsPeriodProblem(v.from, v.to);
  if (p) ctx.addIssue({ code: "custom", path: ["to"], message: p });
}

export const ABC_METRICS = ["revenue", "margin", "qty"] as const;
export type AbcMetric = (typeof ABC_METRICS)[number];

/** Curva ABC (RF-BI-12): período (padrão 12 meses), métrica, canais e situações. Margem exige products:read_cost. */
export const abcQuery = z
  .object({ ...period, metric: z.enum(ABC_METRICS, "Métrica inválida").default("revenue") })
  .superRefine(refinePeriod);
export type AbcQuery = z.output<typeof abcQuery>;

const days = (min: number, max: number, what: string) =>
  z.coerce
    .number(`${what} inválido`)
    .int(`${what} inválido`)
    .min(min, `${what}: de ${min} a ${max} dias`)
    .max(max, `${what}: de ${min} a ${max} dias`);

/** Clientes (RF-BI-11): novos × recorrentes no período e carteira na data final (padrão hoje). */
export const customersQuery = z
  .object({
    ...period,
    activeDays: days(7, 365, "Ativo até").default(90),
    riskDays: days(14, 730, "Em risco até").default(180),
  })
  .superRefine((v, ctx) => {
    refinePeriod(v, ctx);
    if (v.riskDays <= v.activeDays)
      ctx.addIssue({
        code: "custom",
        path: ["riskDays"],
        message: "O limite de “em risco” deve ser maior que o de “ativo”",
      });
  });
export type CustomersQuery = z.output<typeof customersQuery>;

/** Cohort (RF-BI-11): `months` safras (6 a 24) até o mês `month` (padrão o atual). */
export const cohortQuery = z.object({
  month: z
    .string()
    .regex(/^(19|2[01])\d{2}-(0[1-9]|1[0-2])$/, "Mês inválido")
    .optional(),
  months: z.coerce.number("Meses inválido").int("Meses inválido").min(6).max(24).default(12),
  channels,
  statuses,
});
export type CohortQuery = z.output<typeof cohortQuery>;

/** Produtos (RF-BI-10): janela da média de venda e "sem movimentação há N dias". */
export const productInsightsQuery = z.object({
  salesDays: days(7, 365, "Janela de vendas").default(90),
  idleDays: days(7, 730, "Sem movimentação").default(90),
});
export type ProductInsightsQuery = z.output<typeof productInsightsQuery>;

export const BI_EXPORT_REPORTS = ["abc", "customers", "products"] as const;
export type BiExportReport = (typeof BI_EXPORT_REPORTS)[number];
export const BI_FILE_FORMATS = ["csv", "xlsx"] as const;

/**
 * Exportação (mecanismo da T3.6): `params` são os mesmos parâmetros da tela (texto), validados de novo pelo schema do
 * relatório na rota e no job.
 */
export const biExportInput = z.strictObject({
  report: z.enum(BI_EXPORT_REPORTS, "Relatório inválido"),
  params: z.record(z.string().max(40), z.string().max(1_000)).default({}),
  fileFormat: z.enum(BI_FILE_FORMATS, "Formato inválido").default("csv"),
});
export type BiExportInput = z.output<typeof biExportInput>;

export const BI_EXPORT_SCHEMAS = {
  abc: abcQuery,
  customers: customersQuery,
  products: productInsightsQuery,
} as const;

// ----- resultados (valores acima de 2^53 viram null, como no dashboard) -----

export type AnalyticsChannel = { key: string; label: string };

export type AnalyticsStatus = {
  backend: "local" | "r2sql" | "disabled";
  /** Último lote confirmado no lago (ISO) — os dados das telas do histórico vão até aqui. */
  lastExportAt: string | null;
  /** Pedidos alterados ainda não enviados ao lago. */
  pendingOrders: number;
  channels: AnalyticsChannel[];
  statuses: SalesOrderStatusRef[];
  saleStatusIds: string[];
};

export type AbcRowView = {
  productId: string;
  name: string;
  sku: string | null;
  cls: AbcClass;
  value: number | null;
  sharePct4: number;
  cumulativePct4: number;
  orders: number | null;
  qtyQ4: number | null;
  revenueCents: number | null;
  /** Só com products:read_cost. */
  costCents?: number | null;
  marginCents?: number | null;
};
export type AbcResult = {
  period: { from: string; to: string };
  metric: AbcMetric;
  lastExportAt: string | null;
  total: number | null;
  summary: Record<AbcClass, { count: number; value: number | null; sharePct4: number }>;
  /** Produtos vendidos sem custo cadastrado (fora da curva de margem). */
  noCostProducts: number;
  /** Mais produtos do que a resposta traz (o resumo conta todos). */
  truncated: boolean;
  rows: AbcRowView[];
};

export type NewVsReturningRow = {
  month: string;
  newCustomers: number;
  returningCustomers: number;
  newRevenueCents: number | null;
  returningRevenueCents: number | null;
};
export type PortfolioRowView = {
  contactId: string;
  name: string;
  lastDay: string;
  recencyDays: number;
  orders: number | null;
  revenueCents: number | null;
};
export type CustomersResult = {
  period: { from: string; to: string };
  lastExportAt: string | null;
  thresholds: { activeDays: number; riskDays: number };
  months: NewVsReturningRow[];
  portfolio: Record<PortfolioBucket, { customers: number; revenueCents: number | null }>;
  /** Clientes por faixa (até 50, por receita) — só com contacts:read. */
  lists?: Record<PortfolioBucket, PortfolioRowView[]>;
};

export type CohortResult = {
  lastExportAt: string | null;
  months: string[];
  cohorts: {
    month: string;
    size: number;
    cells: { offset: number; customers: number; retentionPct4: number }[];
  }[];
};

export type ProductInsightRow = {
  productId: string;
  name: string;
  sku: string | null;
  unit: string | null;
  physicalQ4: number;
  availableQ4: number;
  onOrderQ4: number;
  soldQ4: number;
  avgDailyQ4: number;
  coverageDays: number | null;
  band: CoverageBand;
  lastMovementDay: string | null;
  /** Só com products:read_cost. */
  valueCents?: number | null;
};
export type ProductInsightsResult = {
  today: string;
  salesDays: number;
  idleDays: number;
  products: number;
  bands: Record<CoverageBand, number>;
  /** Só com products:read_cost. */
  stockValueCents?: number | null;
  idleValueCents?: number | null;
  idleCount: number;
  /** Em ruptura ou com até 30 dias de cobertura, da menor cobertura (até 200). */
  coverage: ProductInsightRow[];
  /** Saldo parado há N dias, do maior valor/saldo (até 200). */
  idle: ProductInsightRow[];
  /** Maior valor imobilizado (até 200) — só com custo. */
  value?: ProductInsightRow[];
  /** Todos os produtos, por nome (só na exportação). */
  all?: ProductInsightRow[];
  truncated: boolean;
};
